import React, { useRef, useEffect } from 'react';
import { ELEMENT_TYPE_GROUPS, TEMPLATE_ELEMENT_KEYS } from './constants';
import { ELEMENT_ICONS } from './elementIcons';
import { typography, borders, textColors, surfaces } from '../../../design-system/tokens';

export const ElementTypesList = ({ selectedType, onSelectType, isCompact, showLabelsOnHover, allowedTypes }) => {
  const firstButtonRef = useRef(null);

  // Focus first button on mount for keyboard navigation
  useEffect(() => {
    firstButtonRef.current?.focus();
  }, []);
  
  // Filter groups and their elements based on allowedTypes prop
  const filteredGroups = ELEMENT_TYPE_GROUPS.map(group => ({
    ...group,
    elements: group.elements.filter(el => !allowedTypes || allowedTypes.includes(el.key))
  })).filter(group => group.elements.length > 0);

  let buttonIndex = 0; // Track button index across groups for first button ref

  return (
    <div className={`flex flex-col ${isCompact ? 'gap-3' : 'gap-4'}`}>
      {filteredGroups.map((group, groupIndex) => {
        const isTemplateGroup = group.title === 'Templates';
        
        return (
          <div key={group.title}>
            {/* Section Header - hide in compact mode */}
            {!isCompact && (
              <div className={`mb-2 pb-1.5 border-b ${borders.light}`}>
                <h3 className={`${typography.label} ${textColors.secondary} uppercase tracking-wide`}>
                  {group.title}
                </h3>
              </div>
            )}
            
            {/* Element Buttons */}
            <div className={`flex flex-col ${isCompact ? 'gap-1.5' : 'gap-2'}`}>
              {group.elements.map((elementType) => {
                const Icon = ELEMENT_ICONS[elementType.key];
                const isSelected = selectedType === elementType.key;
                const isTemplate = TEMPLATE_ELEMENT_KEYS.includes(elementType.key);
                const currentButtonIndex = buttonIndex++;
                
                return (
                  <button
                    key={elementType.key}
                    ref={currentButtonIndex === 0 ? firstButtonRef : null}
                    onClick={() => onSelectType(elementType.key)}
                    className={`
                      flex items-center rounded-lg border transition-all
                      ${isCompact 
                        ? 'p-2 gap-2' 
                        : 'items-start gap-3 p-3'
                      }
                      ${isSelected 
                        ? `${borders.strong} ${surfaces.active} ring-2 ring-gray-200 dark:ring-dark-600` 
                        : isTemplate
                          ? `border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-950 hover:bg-gray-100 dark:hover:bg-dark-900`
                          : `${borders.default} ${surfaces.tertiary} ${surfaces.hover}`
                      }
                    `}
                    title={isCompact && !showLabelsOnHover ? elementType.label : undefined}
                  >
                    <Icon className={`flex-shrink-0 w-5 h-5 ${isSelected ? `${textColors.primary}` : `${textColors.tertiary}`}`} />
                    {/* Normal mode: show both label and description */}
                    {!isCompact && (
                      <div className="flex flex-col items-start text-left">
                        <span className={`font-medium ${textColors.primary}`}>{elementType.label}</span>
                        <span className={`${typography.caption} ${textColors.tertiary} mt-0.5`}>{elementType.description}</span>
                      </div>
                    )}
                    {/* Compact mode with hover: show only label (no description) */}
                    {isCompact && showLabelsOnHover && (
                      <span className={`font-medium ${textColors.primary} whitespace-nowrap`}>{elementType.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ElementTypesList;
